import { supabase } from '../../shared/supabase/supabaseClient';
import { useAuthStore } from '../../auth/store/useAuthStore';
import type { BranchAccess, BranchPermission } from './store/useSessionStore';
import { defaultPermissions } from './store/useSessionStore';
import type { GlobalFeatureFlagsMap } from '../feature-flags/domain/globalFeatureFlags';
import type { PlanEntitlementsRow } from '../feature-flags/domain/planEntitlements';
import type { AccountSetupState } from '../../auth/domain/accountSetup';
import { parseBusinessProfilePayload, type OrganizationBusinessProfile } from './domain/businessProfile';
import { normalizeEntitlementsRpcPayload } from '../feature-flags/planEntitlementsService';
import { parseAccountSetupPayload } from '../../auth/domain/accountSetup';
import type { SessionEmployeeRecord } from '../../auth/application/sessionEmployeeTypes';
import { recordStartupNetworkCall } from '../../shared/dev/networkCallAudit';
import { SESSION_CACHE_MS } from './sessionCacheTtl';
import {
  parseListMyOrganizationsPayload,
  type ListMyOrganizationsPayload,
} from './organizationsPayload';
import { parseContext as parseImpersonationContext, type ImpersonationContext } from '../support/impersonationSession';
import { parseOrgTrustStatus, type OrgTrustStatus } from '../../settings/infra/orgTrustStatusService';

export type AppUpdateConfig = {
  required: boolean;
  min_version_android: string;
  min_version_ios: string;
  store_url_android: string | null;
  store_url_ios: string | null;
  message: string;
};

export type BootstrapMobileSessionPayload = {
  account_setup: AccountSetupState | null;
  entitlements: PlanEntitlementsRow | null;
  feature_flags: GlobalFeatureFlagsMap | null;
  business_profile: OrganizationBusinessProfile | null;
  branches: BranchAccess[];
  employee: SessionEmployeeRecord;
  organization_closed_at: string | null;
  app_update: AppUpdateConfig | null;
  /** Plegado desde 20262024200000: lista de orgs del switcher (list_my_organizations).
   *  undefined = server viejo sin la sección → el lector cae a la RPC directa. */
  organizations: ListMyOrganizationsPayload | null | undefined;
  /** Plegado: sesión de impersonación activa (get_my_active_impersonation_context). */
  impersonation: ImpersonationContext | null | undefined;
  /** Plegado: trust status de la org (get_my_organization_trust_status). */
  org_trust: OrgTrustStatus | null | undefined;
};

let bootstrapInFlight: Promise<BootstrapMobileSessionPayload | null> | null = null;
let bootstrapCache: { value: BootstrapMobileSessionPayload; fetchedAt: number; userId: string } | null = null;

const BOOTSTRAP_CACHE_MS = SESSION_CACHE_MS;

/** Bootstrap RPC puede ir atrasado vs defaultPermissions del cliente (ej. Expenses).
 *  Para el owner, fusionamos campo por campo con OR: si el default tiene true, se mantiene true
 *  aunque el backend devuelva false o undefined. */
function mergeOwnerDefaultPermissions(permissions: BranchPermission[]): BranchPermission[] {
  const byModule = new Map(permissions.map((p) => [p.module, p]));
  return defaultPermissions.map((def) => {
    const existing = byModule.get(def.module);
    if (!existing) return { ...def };
    return {
      module: def.module,
      canView: existing.canView || def.canView,
      canEdit: existing.canEdit || def.canEdit,
      canVoid: existing.canVoid || def.canVoid,
      canDiscount: existing.canDiscount || def.canDiscount,
      canManageAgenda: existing.canManageAgenda || def.canManageAgenda,
      canViewOthers: existing.canViewOthers ?? def.canViewOthers ?? true,
      actions: { ...(def.actions ?? {}), ...(existing.actions ?? {}) },
    };
  });
}

function parsePermissionActions(raw: unknown): Record<string, boolean> | undefined {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (value === true || value === 'true') out[key] = true;
    else if (value === false || value === 'false') out[key] = false;
  }
  return out;
}

function parseManualDiscountNumber(raw: unknown, fallback: number): number {
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

function parseBranchManualDiscountFields(branch: Record<string, unknown>): Pick<
  BranchAccess,
  'manualDiscountMaxPercent' | 'manualDiscountMaxAmount'
> {
  return {
    manualDiscountMaxPercent: parseManualDiscountNumber(
      branch.manualDiscountMaxPercent ?? branch.manual_discount_max_percent,
      20
    ),
    manualDiscountMaxAmount: parseManualDiscountNumber(
      branch.manualDiscountMaxAmount ?? branch.manual_discount_max_amount,
      5000
    ),
  };
}

export function clearBootstrapSessionCache(): void {
  bootstrapInFlight = null;
  bootstrapCache = null;
}

export function primeBootstrapSessionCache(userId: string, payload: BootstrapMobileSessionPayload): void {
  bootstrapCache = { value: payload, fetchedAt: Date.now(), userId };
}

/**
 * Lectura sin fetch: devuelve el cache si es del usuario y tiene < maxAgeMs.
 * Para lectores que aprovechan un bootstrap recién hecho sin gatillar red.
 */
export function peekBootstrapSessionCache(
  userId: string,
  maxAgeMs: number,
): BootstrapMobileSessionPayload | null {
  if (
    !bootstrapCache ||
    bootstrapCache.userId !== userId ||
    Date.now() - bootstrapCache.fetchedAt >= maxAgeMs
  ) {
    return null;
  }
  return bootstrapCache.value;
}

export async function fetchBootstrapMobileSession(
  userId?: string,
  options?: { force?: boolean },
): Promise<BootstrapMobileSessionPayload | null> {
  const uid = userId ?? useAuthStore.getState().session?.user?.id;
  if (!uid) return null;

  const force = options?.force === true;
  const now = Date.now();
  if (
    !force &&
    bootstrapCache &&
    bootstrapCache.userId === uid &&
    now - bootstrapCache.fetchedAt < BOOTSTRAP_CACHE_MS
  ) {
    return bootstrapCache.value;
  }
  if (bootstrapInFlight) return bootstrapInFlight;

  bootstrapInFlight = fetchBootstrapMobileSessionRemote().finally(() => {
    bootstrapInFlight = null;
  });
  const result = await bootstrapInFlight;
  if (result && uid) {
    bootstrapCache = { value: result, fetchedAt: Date.now(), userId: uid };
  }
  return result;
}

async function fetchBootstrapMobileSessionRemote(): Promise<BootstrapMobileSessionPayload | null> {
  recordStartupNetworkCall('session_bootstrap', 'bootstrap_mobile_session');
  const { data, error } = await supabase.rpc('bootstrap_mobile_session');
  if (error) {
    console.warn('[bootstrapMobileSession]', error.message);
    return null;
  }
  if (!data || typeof data !== 'object') return null;

  const raw = data as Record<string, unknown>;
  const account_setup = parseAccountSetupPayload(raw.account_setup ?? null);
  const entitlements = normalizeEntitlementsRpcPayload(raw.entitlements ?? null);
  const feature_flags =
    raw.feature_flags != null && typeof raw.feature_flags === 'object'
      ? (raw.feature_flags as GlobalFeatureFlagsMap)
      : null;

  const branchesRaw = Array.isArray(raw.branches) ? raw.branches : [];
  const branchesParsed = branchesRaw
    .filter((b): b is Record<string, unknown> => b != null && typeof b === 'object')
    .map((b) => {
      const permissionsRaw = Array.isArray(b.permissions) ? b.permissions : [];
      const permissions = permissionsRaw
        .filter((row): row is Record<string, unknown> => row != null && typeof row === 'object')
        .map((row) => ({
          module: String(row.module) as BranchAccess['permissions'][number]['module'],
          canView: Boolean(row.canView ?? row.can_view),
          canEdit: Boolean(row.canEdit ?? row.can_edit),
          canVoid:
            row.canVoid == null && row.can_void == null
              ? undefined
              : Boolean(row.canVoid ?? row.can_void),
          canDiscount:
            row.canDiscount == null && row.can_discount == null
              ? undefined
              : Boolean(row.canDiscount ?? row.can_discount),
          canManageAgenda:
            row.canManageAgenda == null && row.can_manage_agenda == null
              ? undefined
              : Boolean(row.canManageAgenda ?? row.can_manage_agenda),
          canViewOthers:
            row.canViewOthers == null && row.can_view_others == null
              ? undefined
              : Boolean(row.canViewOthers ?? row.can_view_others),
          actions: parsePermissionActions(row.actions),
        }));
      return {
        id: String(b.id),
        name: String(b.name ?? ''),
        location: String(b.location ?? b.address ?? 'Dirección no cargada'),
        permissions,
        isActive: b.isActive === false || b.is_active === false ? false : true,
        stockSalePolicy: (b.stockSalePolicy ?? b.stock_sale_policy ?? 'block') as BranchAccess['stockSalePolicy'],
        barcodeInputMode: (b.barcodeInputMode ?? b.barcode_input_mode ?? 'both') as BranchAccess['barcodeInputMode'],
        scaleBarcodeProfile: (b.scaleBarcodeProfile ??
          b.scale_barcode_profile ??
          'gs1_prefix') as BranchAccess['scaleBarcodeProfile'],
        consolidateCartLines: (b.consolidateCartLines ?? b.consolidate_cart_lines) !== false,
        kitchenFireMode: ((b.kitchenFireMode ?? b.kitchen_fire_mode) === 'before_payment'
          ? 'before_payment'
          : 'after_payment') as BranchAccess['kitchenFireMode'],
        ...parseBranchManualDiscountFields(b),
      };
    });

  const isOwner = entitlements?.is_owner === true;
  const branches = isOwner
    ? branchesParsed.map((branch) => ({
        ...branch,
        permissions: mergeOwnerDefaultPermissions(branch.permissions),
      }))
    : branchesParsed;

  const empRaw = raw.employee;
  const employee: SessionEmployeeRecord =
    empRaw != null && typeof empRaw === 'object'
      ? {
          employeeId: (empRaw as { employee_id?: string | null }).employee_id ?? null,
          isEmployee: Boolean((empRaw as { is_employee?: boolean }).is_employee),
          isActive: (empRaw as { is_active?: boolean }).is_active !== false,
          isGrant: Boolean((empRaw as { is_grant?: boolean }).is_grant),
          grantRole:
            typeof (empRaw as { grant_role?: unknown }).grant_role === 'string'
              ? ((empRaw as { grant_role: string }).grant_role)
              : null,
          permissionsUpdatedAt:
            typeof (empRaw as { permissions_updated_at?: unknown }).permissions_updated_at === 'string'
              ? ((empRaw as { permissions_updated_at: string }).permissions_updated_at)
              : null,
        }
      : { employeeId: null, isEmployee: false, isActive: true, isGrant: false, permissionsUpdatedAt: null };

  const business_profile = parseBusinessProfilePayload(raw.business_profile ?? null);

  const organization_closed_at =
    typeof raw.organization_closed_at === 'string' ? raw.organization_closed_at : null;

  const app_update =
    raw.app_update != null && typeof raw.app_update === 'object'
      ? (raw.app_update as AppUpdateConfig)
      : null;

  return {
    account_setup,
    entitlements,
    feature_flags,
    business_profile,
    branches,
    employee,
    organization_closed_at,
    app_update,
    organizations:
      'organizations' in raw ? parseListMyOrganizationsPayload(raw.organizations) : undefined,
    impersonation:
      'impersonation' in raw ? parseImpersonationContext(raw.impersonation) : undefined,
    org_trust: 'org_trust' in raw ? parseOrgTrustStatus(raw.org_trust) : undefined,
  };
}
